import { datadogRum } from '@datadog/browser-rum'
import { SlotAvailabilityType } from 'app/Bookings/constants'
import {
  isSlotOutsideBookingWindow,
  ParsedFilterType,
} from 'app/ProviderSearch/utils'
import {
  LOGIN_COUNT_KEY,
  PAGINATION_LIMIT,
  USER_PROVIDERS,
} from 'constants/api'
import {
  COOKIE_KEYS,
  dateFormats,
  DayOfWeek,
  PractitionerTitle,
  practitionerTypes,
  providerSearchNumber,
  seFriendlyPractitionerTypes,
  sessionStorageKeys,
  TimeOfDay,
  USER_GEO_IP_LOCATION_KEY,
  UserTypes,
} from 'constants/common'
import { LogEvent } from 'constants/logEvents'
import { TWPERMISSIONS } from 'dataSource/auth/types'
import {
  addDays,
  differenceInYears,
  format,
  getDay,
  getHours,
  isValid,
  parse,
  parseISO,
} from 'date-fns'
import { formatInTimeZone, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz'
import enUS from 'date-fns/locale/en-US'
import { CustomAccessToken } from 'hooks/useThriveAuth/types'
import { ServerResponse } from 'http'
import jwtDecode from 'jwt-decode'
import escapeRegExp from 'lodash/escapeRegExp'
import _omit from 'lodash/omit'
import _startCase from 'lodash/startCase'
import { NextRouter } from 'next/router'
import qs from 'qs'
import { ParsedUrlQuery } from 'querystring'
import { MutableRefObject } from 'react'
import { Req } from 'use-http'
import { getGeocode, getLatLng } from 'use-places-autocomplete'
import { getSafeReturnTo } from 'utils/auth'
import { clearStoredPartnershipCode } from 'utils/b2bDetails'
import { homePageRoutes, pageRoutes } from '../../constants/routing'
import { thrivecareDailyAgendaRedirect } from 'utils/thrivecare'
export * from './getBirthDate'

/*
  formatUserQuery can be used to parse any user specified
  query not necessarily a router query
 */
export const formatUserQuery = (
  query: ParsedUrlQuery | UserQuery
): ParsedUrlQuery => {
  const formattedQuery = {}

  if (!query) {
    return formattedQuery
  }

  Object.keys(query).map((queryKey) => {
    if (queryKey.includes('[]')) {
      const formattedKey = queryKey.replace('[]', '')

      if (Array.isArray(query[queryKey])) {
        formattedQuery[formattedKey] = query[queryKey]
      } else {
        formattedQuery[formattedKey] = [query[queryKey]]
      }
    } else {
      formattedQuery[queryKey] = query[queryKey]
    }
  })

  return formattedQuery
}

/**
 * Parses timeOfDay from a query object into a TimeOfDay array.
 * Handles both single values and arrays from URL query params.
 */
const VALID_TIME_OF_DAY_VALUES = new Set<string>(Object.values(TimeOfDay))

function pickValidTimeOfDayValues(values: string[] | undefined): TimeOfDay[] {
  if (!values?.length) {
    return []
  }

  return values.filter((v): v is TimeOfDay => VALID_TIME_OF_DAY_VALUES.has(v))
}

export const parseTimeOfDayFromQuery = (
  query: ParsedUrlQuery | ReturnType<typeof formatUserQuery>
): TimeOfDay[] => {
  const raw = formatUserQuery(query).timeOfDay

  if (Array.isArray(raw)) {
    return pickValidTimeOfDayValues(raw as string[])
  }

  if (raw && typeof raw === 'string') {
    return pickValidTimeOfDayValues([raw])
  }

  return []
}

export const parseDayOfWeekFromQuery = (
  query: ParsedUrlQuery | ReturnType<typeof formatUserQuery>
): DayOfWeek[] => {
  const raw = formatUserQuery(query).dayOfWeek
  const values = Array.isArray(raw) ? raw : raw ? [raw] : []
  const known = new Set<string>(Object.values(DayOfWeek))

  return values.filter(
    (v): v is DayOfWeek => typeof v === 'string' && known.has(v)
  )
}

/**
 * Build a `ParsedUrlQuery`-compatible object from App Router `useSearchParams()`.
 * Use when a shared component cannot call `useRouter` from `next/router`.
 */
export const parsedUrlQueryFromSearchParams = (
  searchParams: URLSearchParams
): ParsedUrlQuery => {
  const q: ParsedUrlQuery = {}

  searchParams.forEach((value, key) => {
    const prev = q[key]

    if (prev === undefined) {
      q[key] = value
    } else if (Array.isArray(prev)) {
      prev.push(value)
    } else {
      q[key] = [prev as string, value]
    }
  })

  return q
}

interface UserQuery extends ParsedFilterType {
  counselingType?: string[]
  insuranceId?: string
  limit?: string
  offset?: string
  practiceId?: string
  selfPay?: boolean
  latitude?: string
  longitude?: string
  state?: string
  patientId?: string
  location?: string
  appointmentMode?: string
  status?: string
  /** Deep link: opens the profile calendar (date/time) modal on provider profile (WordPress / marketing). */
  showAllSlots?: string
}

export const modifyURLQuery = (
  router: NextRouter,
  userQuery: UserQuery | ParsedUrlQuery,
  removeExistingQuery = false,
  routerAction = 'push',
  newBasePath?: string
): void => {
  const { asPath } = router
  const parsedQuery = removeExistingQuery ? {} : getParsedQuery(router)
  const parsedUserQuery = formatUserQuery(userQuery)
  const basePath =
    newBasePath !== undefined ? newBasePath : asPath.split('?')[0]
  const parsedPathName = `${basePath}?${qs.stringify(
    {
      ...parsedQuery,
      ...parsedUserQuery,
    },
    { encode: false, arrayFormat: 'brackets', skipNulls: true }
  )}`

  // Next js router doesn't properly stringify nested objects
  // https://github.com/vercel/next.js/pull/2605
  // TODO check with new URL()
  router[routerAction](parsedPathName, undefined, {
    shallow: true,
  })
}

/**
 * This method is used to get the stringified query string with brackets as
 * array format
 * @param userQuery
 * @returns stringifiedQuery
 */

export const getStringifiedQuery = (
  userQuery: UserQuery | ParsedUrlQuery
): string => {
  return qs.stringify(userQuery, {
    encode: false,
    arrayFormat: 'brackets',
    skipNulls: true,
  })
}

// TODO move this to useAPI's response interceptor
export const hasError = (
  request: Req,
  response: CustomResponse,
  defaultMessage = 'Something went wrong! Please try again'
): void => {
  // Sample response for future reference
  // error{}->
  //  message: "Patient already exists."
  // -----  OR ----
  // error{}->
  //  message{} -> { description: "Patient does not have any relationship", patientDetails: {//patientDetails}}
  // -----  OR ----
  // error{}->
  //  address[]->
  //   constraints{}-> { isString: 'error message eg: line2 must be string'}

  if (request.error) {
    if (response?.error) {
      const { error } = response
      const normalizedError = new Error(defaultMessage)
      normalizedError.name = 'Response Error'

      if (error?.message) {
        if (typeof error.message === 'object') {
          normalizedError.message = error.message?.description || defaultMessage

          if (error.message.errorCode) {
            normalizedError.name = error.message.errorCode.toString()
          }
        } else {
          normalizedError.message = error.message
        }
      }

      if ((error?.message as MessageError)?.amdDescription) {
        normalizedError['errorFromAMD'] = true
      }

      const errorKeys = Object.keys(error)
      const singleError = error[errorKeys[0]]

      if (Array.isArray(singleError)) {
        const firstError = singleError[0]

        if (typeof firstError == 'object' && firstError.constraints) {
          const constraintKeys = Object.keys(firstError.constraints)
          normalizedError.message = firstError.constraints[constraintKeys[0]]
        } else if (typeof firstError === 'string') {
          normalizedError.message = firstError
        }
      } else if (typeof singleError === 'string') {
        normalizedError.message = singleError
      }

      const emailError =
        typeof error?.message === 'object' && 'errors' in error.message
          ? error.message.errors?.[0]?.reason
          : undefined

      if (emailError) {
        normalizedError.message = emailError
        throw normalizedError
      }

      throw normalizedError
    }

    throw request.error
  }
}

export const sortGeneratedSlots = (generatedSlots: DateSlot): DateSlot => {
  let sortedslots: DateSlot = {}

  for (const [key, value] of Object.entries(generatedSlots)) {
    const sortedValue = sortByDate(value, 'start', true)
    sortedslots = { ...sortedslots, [key]: sortedValue }
  }

  return sortedslots
}

export const sortByDate = <Type>(
  list: Type[],
  value: string,
  ascending = true
): Type[] => {
  return list?.sort((a, b) =>
    ascending
      ? new Date(a?.[value]).getTime() - new Date(b?.[value]).getTime()
      : new Date(b?.[value]).getTime() - new Date(a?.[value]).getTime()
  )
}

export const fetchDataAsync = (
  parsedQuery: qs.ParsedQs,
  route: string,
  backgroundRequest: Req,
  pagination: PaginationSchema,
  paginationLimit: number = PAGINATION_LIMIT
  // Type any is intentional here
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any>[] => {
  const { count, limit: initialOffet } = pagination
  let offset = initialOffet
  const promises = []

  while (offset < count) {
    const stringfiedQs = qs.stringify(
      {
        ...parsedQuery,
        offset,
        limit: paginationLimit,
      },
      { addQueryPrefix: true, skipNulls: true }
    )
    promises.push(backgroundRequest.get(`${route}${stringfiedQs}`))
    offset += paginationLimit
  }

  return promises
}

/**
 *
 * @param placeId placedId from google places api
 * @param usedForFeature place where the event is being called from.
 * @returns {firstRestult, latLong}
 */
export const getFirstLocationResultData = async (
  placeId: string,
  usedForFeature: string
): Promise<LocationResultType> => {
  datadogRum.addAction(LogEvent.GEOCODE_USED, {
    usedForFeature,
  })
  const results = await getGeocode({ placeId })
  const firstResult = results[0]
  const latLong = await getLatLng(firstResult)
  return { firstResult, latLong }
}

// TODO: Date-fns-tz recently (v1.3.4 and up, dec-2021) introduced formatInZonedTime which is what the getTimeZoneFormattedDate helper function does ..
// SO we can remove it and use formatInZonedTime
export const getTimeZoneFormattedDate = (
  date: Date | string | number,
  dateFormat: string,
  timeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone
): string => {
  const dt = !isNaN(Date.parse(date as string)) ? date : new Date()
  // IF the date variable is a date object then utcToZonedTime function handles removing of the local timezone

  return formatInTimeZone(dt, timeZone, dateFormat, { locale: enUS })
}

export const getCurrentZonnedTime = (timeZone: string): Date => {
  const utc = zonedTimeToUtc(
    new Date(),
    Intl.DateTimeFormat().resolvedOptions().timeZone
  )
  const zonedTime = utcToZonedTime(utc, timeZone)
  return zonedTime
}

export const isDateEqual = (date1: Date, date2: Date): boolean => {
  if (date1 && date2) {
    return (
      format(date1, dateFormats.YEAR_MONTH_DAY_FORMAT) ===
      format(date2, dateFormats.YEAR_MONTH_DAY_FORMAT)
    )
  }

  return false
}

export const isToday = (date: Date | number, timeZone: string): boolean => {
  if (!date) {
    return false
  }

  const today = format(
    getCurrentZonnedTime(timeZone),
    dateFormats.YEAR_MONTH_DAY_FORMAT
  )
  const dateToMatch = format(date, dateFormats.YEAR_MONTH_DAY_FORMAT)

  return today === dateToMatch
}

export const isTomorrow = (date: Date | number, timeZone: string): boolean => {
  if (!date) {
    return false
  }

  const tomorrow = format(
    addDays(getCurrentZonnedTime(timeZone), 1),
    dateFormats.YEAR_MONTH_DAY_FORMAT
  )

  const dateToMatch = format(date, dateFormats.YEAR_MONTH_DAY_FORMAT)

  return tomorrow === dateToMatch
}

export const getProviderProfileParams = (
  provider: Provider,
  practice?: Practice
): SlugBasedProfileParams | PracticeIdBasedProfileParams => {
  let providerProfileParams = {} as
    | SlugBasedProfileParams
    | PracticeIdBasedProfileParams
  const providerPractice = practice ?? provider.practice

  const { state } = providerPractice
  const formattedState = state.replace(/\s/g, '-').toLowerCase()

  if (!!providerPractice.state && !!providerPractice.providerSlugs) {
    let providerTitle = provider.title

    if (!providerTitle) {
      //TODO: Need to check if we can use getProviderTitle util function instead
      //of below code. Currently, using that is throwing a reference error
      providerTitle = provider?.filters?.find(
        (providerFilter) => providerFilter.type === 'provider_title'
      )?.value
    }

    const type = getPractitionerType(providerTitle)
    providerProfileParams = {
      type,
      state: formattedState,
      slug:
        providerPractice.providerSlugs[0].name ||
        providerPractice.providerSlugs[0],
    }
  } else {
    const { id: providerId, firstName, lastName } = provider
    const { id: practiceId, city } = providerPractice
    const providerName = firstName + lastName
    providerProfileParams = {
      state: formattedState,
      city: city.replace(/\s/g, '-').toLowerCase(),
      providerName: providerName.replace(/\s/g, '-').toLowerCase(),
      providerId,
      practiceId,
    }
  }

  return providerProfileParams
}

export const getPractitionerType = (title: string): string => {
  if (
    title === PractitionerTitle.NP ||
    title === PractitionerTitle.Psych_NP ||
    title === PractitionerTitle.PMHNP ||
    title === PractitionerTitle.DNP ||
    title?.includes(PractitionerTitle.PMHNP)
  ) {
    return practitionerTypes.PROVIDER
  } else if (title === PractitionerTitle.DO || title === PractitionerTitle.MD) {
    return practitionerTypes.PSYCHIATRIST
  }

  return practitionerTypes.THERAPIST
}

export const checkPathIsProviderProfileURL = (asPath: string): boolean => {
  const basePath = asPath.split('?')[0]
  const pathSegregation = basePath.split('/').filter((path) => !!path)

  if (
    pathSegregation.length === 3 &&
    Object.values(practitionerTypes).includes(pathSegregation[0]) &&
    pathSegregation[1].length === 2
  ) {
    // This checks if the urlpath has 3 slugs
    // Then checking if the first slug matches our practitioner types
    // Then checking if the 2nd slug matches the length of a state abbreviation
    return true
  }

  return false
}

export const checkPathIsSEFriendlyURL = (asPath: string): boolean => {
  const basePath = asPath.split('?')[0]
  const pathSegregation = basePath.split('/').filter((path) => !!path)

  if (
    pathSegregation.length === 1 &&
    Object.values(seFriendlyPractitionerTypes).includes(pathSegregation[0])
  ) {
    // Checks if its the hub page
    return true
  } else if (
    pathSegregation.length === 3 &&
    Object.values(seFriendlyPractitionerTypes).includes(pathSegregation[0]) &&
    pathSegregation[1].length === 2
  ) {
    // Checks if its the listing page.
    return true
  }

  return false
}

export const calculateAge = (dob: string): number => {
  const date = parseISO(dob)
  const age = differenceInYears(new Date(), date)
  return age
}

export { formatPhoneNumber } from 'utils/formatPhoneNumber'

export const countProviderSlots = (
  providerAvailableSlots: DateSlot,
  bookingWindowHours?: number
): number => {
  const providerSlotsCount = Object.keys(providerAvailableSlots).reduce(
    (totalCount, slotDate) => {
      const validSlotList = providerAvailableSlots[slotDate].filter((slot) => {
        const isValidSlot = slot !== null

        if (bookingWindowHours !== undefined) {
          return (
            isValidSlot &&
            isSlotOutsideBookingWindow(slot?.start, bookingWindowHours)
          )
        }

        return isValidSlot
      })

      return (totalCount = totalCount + validSlotList?.length)
    },
    0
  )
  return providerSlotsCount
}

export const countProviderSlotsShown = (
  providerAvailableSlots: DateSlot
): number => {
  const providerSlotsCount = Object.keys(providerAvailableSlots).reduce(
    (totalCount, slotDate) => {
      const notNulls = providerAvailableSlots[slotDate].filter((slot) => {
        return slot !== null
      })
      return (totalCount = totalCount + notNulls?.length)
    },
    0
  )
  return providerSlotsCount
}

// Checks if a given string is valid date
export const isDateValid = (date: string): boolean => !isNaN(Date.parse(date))

export const getFormattedPracticeAddress = (practice: Practice): Practice => {
  const formattedName = _startCase(practice.name?.toLowerCase())

  const formattedAddress = practice.address?.replace(/, US[A]?/, '') //Replaces US, USA from the address

  const formattedAddressLine1 = practice.addressLine1
    ?.split(', ')
    .map((addressPart) => _startCase(addressPart.toLowerCase()))
    .join(', ')

  return {
    ...practice,
    name: formattedName,
    address: formattedAddress,
    addressLine1: formattedAddressLine1,
  }
}

const getStreetAddressFromPractice = (practice) => {
  const { address, city, state, zipcode, country } = practice
  const addressComponents = address.split(',')
  // Escape dynamic values before RegExp construction (SAST-104 hygiene / ReDoS).
  // No /g — these are reused with .test() across address components; a global
  // flag would leave lastIndex set and can skip later matches.
  const stateRe = new RegExp(escapeRegExp(state))
  const countryRe = new RegExp(escapeRegExp(country) + '[A]?')
  const zipcodeRe = new RegExp(escapeRegExp(zipcode))

  const filteredAddressComponents = addressComponents
    .map((addressComponent) => addressComponent.trim())
    .filter(
      (addressComponent) =>
        !(
          city === addressComponent ||
          stateRe.test(addressComponent) ||
          zipcodeRe.test(addressComponent) ||
          countryRe.test(addressComponent)
        )
    )

  return filteredAddressComponents
}

export const getPracticeAddressFeatures = (
  practice: Practice
): {
  practiceName: string
  aptOrSuite: string
  streetAddress: string
  city: string
  state: string
  country: string
  zipcode: string
  fullAddress: string
  googlePlaceId: string | null
} => {
  const {
    name,
    addressLine1,
    city,
    state,
    zipcode,
    country,
    googlePlaceId,
  } = practice

  const formattedName = _startCase(name?.toLowerCase())
  const rawStreetAddress = getStreetAddressFromPractice(practice)
  const streetAddress = rawStreetAddress.join(', ')

  const formattedAptOrSuite = addressLine1
    ?.split(', ')
    .map((addressPart) => _startCase(addressPart.toLowerCase()))
    .join(', ')

  const formattedZipcode = zipcode?.slice(0, 5)

  const addressEnd = `${city}, ${state} ${formattedZipcode}`
  const fullAddress = formattedAptOrSuite
    ? `${streetAddress}, ${formattedAptOrSuite}, ${addressEnd}`
    : `${streetAddress}, ${addressEnd}`

  return {
    practiceName: formattedName,
    aptOrSuite: formattedAptOrSuite,
    streetAddress,
    city,
    state,
    country,
    zipcode: formattedZipcode,
    fullAddress,
    googlePlaceId,
  }
}

export const getDisplayBusinessName = (wpBusinessName?: string): string =>
  wpBusinessName?.trim() || 'Thriveworks Counseling'

export const getUserLocation = (): string =>
  sessionStorage.getItem(USER_GEO_IP_LOCATION_KEY)

export const getConcatenatedPatientNames = (
  patients: PatientDetails[]
): string => {
  if (patients?.length === 1) {
    return getPatientName(patients[0])
  }

  const patientNamesArr = patients
    ?.map((patient) => getPatientName(patient))
    .sort((name1, name2) => name1.localeCompare(name2))

  const patientNamesLength = patientNamesArr?.length - 1
  const restOfPatients = patientNamesArr?.slice(0, patientNamesLength)
  return `${restOfPatients?.join(', ')} and ${
    patientNamesArr?.[patientNamesLength]
  }`
}

const getPatientName = (patient: PatientDetails) => {
  return `${patient.firstName} ${patient.lastName}`
}

// Strips pathname from query params and parsed Query in form of object
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getParsedQuery = (router: NextRouter): Record<string, any> => {
  const { asPath } = router
  const queryString = asPath.split('?')[1]
  const parsedQueryObj = qs.parse(queryString)
  return parsedQueryObj
}

export const clearNonPersistingDataInSession = (): void => {
  sessionStorage.removeItem(sessionStorageKeys.PROVIDER_SEARCH_QUERY)
}

/**
 * Last provider-search query object persisted while on /app/providers (location, filters, etc.).
 */
export const getStoredProviderSearchQuery = (): Record<string, unknown> => {
  if (typeof window === 'undefined') {
    return {}
  }

  try {
    const raw = sessionStorage.getItem(sessionStorageKeys.PROVIDER_SEARCH_QUERY)

    if (!raw) {
      return {}
    }

    const parsed: unknown = JSON.parse(raw)

    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>
    }

    return {}
  } catch {
    return {}
  }
}

/**
 * Persists the chosen insurance into the PROVIDER_SEARCH_QUERY session entry so
 * the booking funnel preselects it. The flow that skip provider search page (e.g. blind booking) must
 * write it explicitly. Passing a falsy id (self-pay) drops any stale insurance.
 */
export const persistInsuranceSelectionToSearchQuery = (
  insuranceId?: string | null
): void => {
  if (typeof window === 'undefined') {
    return
  }

  const storedQuery = getStoredProviderSearchQuery()
  const nextQuery = insuranceId
    ? { ...storedQuery, insuranceId }
    : _omit(storedQuery, ['insuranceId'])

  try {
    sessionStorage.setItem(
      sessionStorageKeys.PROVIDER_SEARCH_QUERY,
      JSON.stringify(nextQuery)
    )
  } catch {
    // storage failures must not block booking handoff.
  }
}

type BuildProviderSearchUrlOverrides = {
  areaOfConcern?: string[]
  availabilityType?: string[]
  /** Filter id(s) for Medication/Psychiatry; replaces any counselingType from session. */
  counselingType?: string[]
  /** Replaces the stored search state. Used when results must follow a practice, not the last search. */
  state?: string
  latitude?: string
  longitude?: string
  location?: string
}

/**
 * Builds /app/providers URL merging session-stored search params with optional filter overrides
 * (e.g. ADHD / in-person for stimulant notices). Resets result pagination by default.
 */
export const buildProviderSearchUrlWithStoredQuery = (
  overrides: BuildProviderSearchUrlOverrides = {},
  options: { resetPagination?: boolean } = {}
): string => {
  const { resetPagination = true } = options
  const merged = getStoredProviderSearchQuery()

  if (overrides.areaOfConcern?.length) {
    merged.areaOfConcern = overrides.areaOfConcern
  }

  if (overrides.availabilityType?.length) {
    merged.availabilityType = overrides.availabilityType
  }

  if (overrides.state) {
    // A state override means results must follow that state. Drop stored geo
    // fields first so an absent city/coords cannot leave a mismatched location.
    delete merged.latitude
    delete merged.longitude
    delete merged.location
    merged.state = overrides.state
  }

  if (overrides.latitude) {
    merged.latitude = overrides.latitude
  }

  if (overrides.longitude) {
    merged.longitude = overrides.longitude
  }

  if (overrides.location) {
    merged.location = overrides.location
  }

  // SearchPage hydrates from browser/IP when any of state/lat/lng/location is
  // missing, and that overwrite replaces the practice pin. If the practice has
  // no city, keep the tuple complete (or geocode this state) instead of adding
  // a cross-promo query flag — ordinary `state` is also stored search state.
  if (overrides.state && !merged.location) {
    merged.location = overrides.state
  }

  if (resetPagination) {
    merged.limit = PAGINATION_LIMIT.toString()
    merged.offset = '0'
  }

  // Drop session counselingType so we can pin to Medication/Psychiatry when provided.
  let mergedForQuery = _omit(merged, ['counselingType'])

  if (overrides.counselingType?.length) {
    mergedForQuery = {
      ...mergedForQuery,
      counselingType: overrides.counselingType,
    }
  }

  const queryString = qs.stringify(mergedForQuery as qs.ParsedQs, {
    encode: false,
    arrayFormat: 'brackets',
    skipNulls: true,
  })

  return queryString
    ? `${pageRoutes.PROVIDER_SEARCH}?${queryString}`
    : pageRoutes.PROVIDER_SEARCH
}

export const removeKeywordFromStoredSearchQuery = (): void => {
  clearStoredPartnershipCode()
  const storedProviderSearchQuery = JSON.parse(
    sessionStorage.getItem(sessionStorageKeys.PROVIDER_SEARCH_QUERY)
  )
  sessionStorage.setItem(
    sessionStorageKeys.PROVIDER_SEARCH_QUERY,
    JSON.stringify(_omit(storedProviderSearchQuery, ['partnershipCode']))
  )
}

export const removeVerifiedInsuranceInfo = (): void => {
  sessionStorage.removeItem(sessionStorageKeys.VERIFIED_INSURANCE_AUDIT_ID)
  sessionStorage.removeItem(sessionStorageKeys.VERIFIED_INSURANCE_CARRIER_ID)
}

export const scrollToDiv = (
  containerRef: MutableRefObject<HTMLDivElement>,
  scrollProps?: Record<string, unknown>,
  scrollMethod = 'scrollIntoView'
): void => {
  containerRef?.current?.[scrollMethod]({ behavior: 'smooth', ...scrollProps })
}

export enum ErrorMessage {
  MAX = 'Max',
  NOT_VALID = 'Not Valid',
  REQUIRED = 'Required',
}

export function getErrorMessage(
  type: string,
  value: string,
  isSupportOrThirdPartyUser = false
): string {
  switch (type) {
    case ErrorMessage.REQUIRED:
      if (isSupportOrThirdPartyUser) {
        return `Please enter the ${value}${
          value === 'relationship' ? ' to the client' : ''
        }`
      } else {
        return `Oops, Please enter your ${value}`
      }

    case ErrorMessage.NOT_VALID:
      return `Something's not right, please enter a valid ${value}`

    case ErrorMessage.MAX:
      return `Please enter a valid ${value}`
  }
}

export const isFirstTimeUser = (user: Partial<User>): boolean => {
  const loginCount = user?.[LOGIN_COUNT_KEY] || 0
  const userProviders = user?.[USER_PROVIDERS] || []
  return loginCount === 1 && userProviders.length === 1
}

export const getGenericErrorMessage = (): string => {
  const { formattedPhoneNumber } = providerSearchNumber
  return `Sorry, an error occurred while loading this page. Please refresh the page or call ${formattedPhoneNumber} to speak with a representative`
}

export const removeThriveworksFromString = (string: string): string => {
  return string.replace(/THRIVEWORKS/gi, '')
}

export const doesStringHaveValue = (val: string | undefined): boolean =>
  typeof val === 'string' && val.length > 0

export const consentForMinorText = (childName: string): string =>
  `Parent / legal guardian of ${childName}`

export const getHasFutureSlots = (
  nextAvailabilityForProviders: NextAvailabilityMap
): boolean => {
  return Object.keys(nextAvailabilityForProviders).some(
    (key) => nextAvailabilityForProviders[key] !== null
  )
}

export const splitDateAndTime = (
  formattedDate: Date
): { date: string; time: string } => {
  const validDate = formattedDate instanceof Date ? formattedDate : new Date()
  const dateAndTime = new Date(validDate).toISOString().split('T')

  return {
    date: dateAndTime[0],
    time: dateAndTime[1],
  }
}

export const choosePermissionBasedUserDestination = (
  permissions: TWPERMISSIONS[],
  redirectUrl?: string,
  options?: {
    isCalendarDailyAgendaEnabled?: boolean
  }
): string => {
  const { isCalendarDailyAgendaEnabled = false } = options || {}

  let routeBasedOnPermission = permissions.find((p) => homePageRoutes[p])
    ? homePageRoutes[permissions.find((p) => homePageRoutes[p])]
    : pageRoutes.DASHBOARD_USER_HOME

  let safeRedirectUrl = getSafeReturnTo(redirectUrl)

  if (safeRedirectUrl) {
    // Thrivecare landing page temporary redirection, will remove after full calendar launch
    safeRedirectUrl = thrivecareDailyAgendaRedirect(
      safeRedirectUrl,
      isCalendarDailyAgendaEnabled
    )
    return safeRedirectUrl
  }

  // Thrivecare landing page temporary redirection, will remove after full calendar launch
  routeBasedOnPermission = thrivecareDailyAgendaRedirect(
    routeBasedOnPermission,
    isCalendarDailyAgendaEnabled
  )

  return routeBasedOnPermission
}

export function sortedStringify(obj: Record<string, unknown>): string {
  if (typeof obj !== 'object' || obj === null) {
    return JSON.stringify(obj)
  }

  const keys = Object.keys(obj).sort()
  const sortedObj = keys.reduce<Record<string, unknown>>((result, key) => {
    const val = obj[key]

    if (isRecord(val)) {
      result[key] = sortedStringify(val)
    } else {
      result[key] = obj[key]
    }

    return result
  }, {})

  return JSON.stringify(sortedObj)
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

export function hashSfAddress(address: LeadAddress): string {
  // Convert the address object into a sorted string
  const addressString = sortedStringify({ ...address })

  // Encode the string into base64
  const hash = btoa(addressString)

  return hash
}

export function getNumbersOnlyFromString(
  numberString?: string
): string | undefined {
  return numberString ? numberString.replace(/[^0-9]/g, '') : undefined
}

export function convertHtmlToString(htmlString: string): string {
  // Use a regular expression to remove HTML tags
  return htmlString.replace(/<[^>]+>/g, '').trim()
}

export const filterDateSlotsByAppointmentMode = (
  dateSlots: DateSlot,
  appointmentMode: SlotAvailabilityType
): DateSlot => {
  const dates = Object.keys(dateSlots)
  const filteredSlots = {}

  dates.map((date) => {
    const slots = dateSlots[date].map((slot) => {
      return appointmentMode === undefined
        ? slot
        : slot.availabilityType.includes(appointmentMode) && slot
    })

    filteredSlots[date] = slots
  })

  return filteredSlots
}

/**
 * Determines which time-of-day interval a slot falls into based on its start time.
 *
 * Time-of-day intervals:
 * - EARLY_MORNING: before 9am
 * - MORNING: 9am to 12pm
 * - AFTERNOON: 12pm to 5pm
 * - EVENING: after 5pm
 *
 * @param slot - The slot to check
 * @param timeZone - The timezone to use for time calculations
 * @returns The TimeOfDay enum value for the slot
 */
/** JS weekday: 0 Sunday … 6 Saturday (in provider timezone). */
export const getSlotWeekday = (slot: ProviderSlot, timeZone: string): number =>
  getDay(utcToZonedTime(slot.start, timeZone))

export const getSlotTimeOfDay = (
  slot: ProviderSlot,
  timeZone: string
): TimeOfDay => {
  const slotHour = getHours(utcToZonedTime(slot.start, timeZone))

  if (slotHour < 9) {
    return TimeOfDay.EARLY_MORNING
  }

  if (slotHour < 12) {
    return TimeOfDay.MORNING
  }

  if (slotHour < 17) {
    return TimeOfDay.AFTERNOON
  }

  return TimeOfDay.EVENING
}

export const getSlotWithTimeOfDayAdded = (
  slot: ProviderSlot,
  timeOfDay: TimeOfDay[],
  timeZone: string
): ProviderSlot | null => {
  const slotInterval = getSlotTimeOfDay(slot, timeZone)

  if (timeOfDay.includes(slotInterval)) {
    return {
      ...slot,
      timeOfDay: slotInterval,
    }
  }

  return null
}

export const PG_DOW_TO_DAY_OF_WEEK: Record<number, DayOfWeek> = {
  0: DayOfWeek.SUNDAY,
  1: DayOfWeek.MONDAY,
  2: DayOfWeek.TUESDAY,
  3: DayOfWeek.WEDNESDAY,
  4: DayOfWeek.THURSDAY,
  5: DayOfWeek.FRIDAY,
  6: DayOfWeek.SATURDAY,
}

export const getSlotDayOfWeek = (
  slot: ProviderSlot,
  timeZone: string
): DayOfWeek => {
  const dowIndex = getDay(utcToZonedTime(slot.start, timeZone))
  return PG_DOW_TO_DAY_OF_WEEK[dowIndex]
}

export const getSlotWithDayOfWeekAdded = (
  slot: ProviderSlot,
  dayOfWeek: DayOfWeek[],
  timeZone: string
): ProviderSlot | null => {
  const slotDow = getSlotDayOfWeek(slot, timeZone)

  if (dayOfWeek.includes(slotDow)) {
    return {
      ...slot,
      dayOfWeek: slotDow,
    }
  }

  return null
}

/**
 * Adds timeOfDay property to matching slots WITHOUT filtering.
 * Used in waterfall buckets where TOD filters were removed but we still
 * want to highlight matching slots while showing all slots.
 *
 * @param slot - The slot to process
 * @param timeOfDay - The time-of-day values to highlight
 * @param timeZone - The timezone for slot time calculations
 * @returns The slot with timeOfDay added if matching, otherwise unchanged
 */
export const getSlotWithTimeOfDayHighlight = (
  slot: ProviderSlot,
  timeOfDay: TimeOfDay[],
  timeZone: string
): ProviderSlot => {
  const slotInterval = getSlotTimeOfDay(slot, timeZone)

  // Add timeOfDay if it matches, but always return the slot (no filtering)
  if (timeOfDay.includes(slotInterval)) {
    return {
      ...slot,
      timeOfDay: slotInterval,
    }
  }

  return slot
}

/**
 * Checks which time-of-day intervals a provider has slots for.
 * Used to show "bonus" indicators when providers in waterfall buckets
 * match removed time-of-day filters.
 *
 * @param generatedSlots - The provider's slots grouped by date
 * @param targetTimeOfDay - The time-of-day values to check for
 * @param timeZone - The timezone to use for slot time calculations
 * @returns Array of matched TimeOfDay values
 */
export const providerHasTimeOfDaySlots = (
  generatedSlots: DateSlot,
  targetTimeOfDay: TimeOfDay[],
  timeZone: string
): TimeOfDay[] => {
  if (!generatedSlots || !targetTimeOfDay?.length || !timeZone) {
    return []
  }

  const allSlots = Object.values(generatedSlots).flat()
  const matchedTimeOfDay: TimeOfDay[] = []

  for (const slot of allSlots) {
    if (!slot?.start) {
      continue
    }

    const slotInterval = getSlotTimeOfDay(slot, timeZone)

    if (
      targetTimeOfDay.includes(slotInterval) &&
      !matchedTimeOfDay.includes(slotInterval)
    ) {
      matchedTimeOfDay.push(slotInterval)
    }

    // Early exit if we've found all target time-of-day values
    if (matchedTimeOfDay.length === targetTimeOfDay.length) {
      break
    }
  }

  return matchedTimeOfDay
}

/**
 * Extracts TimeOfDay enum values from removed filter display names.
 * Used to determine which time-of-day filters were removed so we can
 * check if providers have matching "bonus" slots.
 *
 * Handles both display names (e.g., "Early Morning") and description values
 * (e.g., "before 9am") for robustness.
 */
export const getRemovedTimeOfDayValues = (
  removedFilters: (string | number)[]
): TimeOfDay[] => {
  if (!removedFilters?.length) {
    return []
  }

  const filterNameToEnum: Record<string, TimeOfDay> = {
    'Early Morning': TimeOfDay.EARLY_MORNING,
    Morning: TimeOfDay.MORNING,
    Afternoon: TimeOfDay.AFTERNOON,
    Evening: TimeOfDay.EVENING,
  }

  return removedFilters
    .filter(
      (filter): filter is string =>
        typeof filter === 'string' && !!filterNameToEnum[filter]
    )
    .map((filter) => filterNameToEnum[filter])
}

export const isValidEmail = (email: string): boolean => {
  return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)
}

export function isValidDate(dateString: string, formatString: string): boolean {
  const parsedDate = parse(dateString, formatString, new Date())
  return isValid(parsedDate) && dateString === format(parsedDate, formatString)
}

/**
 * Converts Web Mercator (EPSG:3857) to latitude/longitude (EPSG:4326)
 */
export function convertMercatorToLatLng(
  x: number,
  y: number
): google.maps.LatLngLiteral {
  const R = 6378137 // Earth's radius in meters (WGS 84)
  const lng = (x / R) * (180 / Math.PI)
  const lat = (Math.atan(Math.exp(y / R)) - Math.PI / 4) * 2 * (180 / Math.PI)
  return { lat, lng }
}

/**
 * Converts latitude/longitude (EPSG:4326) to Web Mercator (EPSG:3857)
 */
export const convertLatLngToMercator = (
  lat: number,
  lng: number
): { x: number; y: number } => {
  const R = 6378137 // Earth's radius in meters (WGS 84)
  const x = ((lng * Math.PI) / 180) * R
  const y = Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 180 / 2)) * R
  return { x, y }
}

/**
 * Sets the access token in the server cookies
 * @param accessToken - The access token to set
 * @param response - The response object to set the cookie on
 */
export const setAccessTokenToServerCookies = (
  accessToken: string,
  response: ServerResponse
): void => {
  const userObject: CustomAccessToken = jwtDecode(accessToken)
  const tokenExpirationDate = new Date(userObject?.exp * 1000)

  const cookieParts = [
    `${COOKIE_KEYS.ACCESS_TOKEN}=${accessToken}`,
    'Path=/',
    `SameSite=${process.env.NODE_ENV === 'development' ? 'Lax' : 'None'}`,
    `Expires=${tokenExpirationDate.toUTCString()}`,
    process.env.NODE_ENV !== 'development' ? 'Secure' : '',
  ]

  response.setHeader('Set-Cookie', cookieParts.filter(Boolean).join('; '))
}

export const isLoggedInUser = (user?: Partial<User> | null): boolean =>
  Boolean(user?.id)

export const isPatientUser = (): boolean => {
  let userType: string = UserTypes.PATIENT

  if (typeof window !== 'undefined') {
    // localStorage can throw in restricted contexts (e.g. Safari private browsing, sandboxed iframes)
    try {
      userType = localStorage.getItem('userType') || UserTypes.PATIENT
    } catch {
      userType = UserTypes.PATIENT
    }
  }

  return userType === UserTypes.PATIENT
}
